import {
  Box,
  Button,
  Checkbox,
  Drawer,
  DrawerBody,
  DrawerContent,
  DrawerFooter,
  DrawerHeader,
  DrawerOverlay,
  Heading,
  IconButton,
  List,
  ListItem,
  Menu,
  MenuButton,
  MenuItem,
  MenuList,
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Stack,
  useDisclosure,
  useToast,
} from '@chakra-ui/react';
import { zodResolver } from '@hookform/resolvers/zod';
import { DataTable, DataTableFilters, DataTableSearch } from '@pelicargo/datatable';
import { useCrud } from '@pelicargo/datatable';
import { Show } from '@pelicargo/ui';
import { Filters } from '@pelicargo/utils';
import { FilterExpressionSnippet } from '@prisma/client';
import { createColumnHelper } from '@tanstack/react-table';
import { ChangeEvent, useCallback, useMemo, useRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import { HiEllipsisVertical, HiXMark } from 'react-icons/hi2';
import z from 'zod';

import { Condition, FilterEditor } from '../components/FilterEditor';
import { SimpleForm } from '../components/SimpleForm';
import { SimpleInput } from '../components/SimpleInput';
import { trpc } from '../config/trpc';

const snippetValidator = z.object({
  description: z.string().nullish(),
  identifier: z.string(),
});

const SnippetModal = ({
  isOpen,
  onClose,
  base,
  refetch,
  sources,
}: {
  isOpen: boolean;
  onClose: () => void;
  base: FilterExpressionSnippet;
  sources: Filters.DataSources[];
  refetch: () => void;
}) => {
  const { mutateAsync, isLoading } = trpc.upsertSnippet.useMutation();
  const condition = useRef<Condition>();

  const toast = useToast();

  const methods = useForm<z.infer<typeof snippetValidator>>({
    mode: 'onSubmit',
    resolver: zodResolver(snippetValidator),
    defaultValues: {
      description: base?.description,
      identifier: base?.identifier,
    },
  });

  const onValid = async (form: z.infer<typeof snippetValidator>) => {
    if (!condition.current) return;

    try {
      await mutateAsync({
        filter: condition.current.check(),
        identifier: form.identifier,
        description: form.description,
        sources,
      });
      refetch();
      onClose();
    } catch (e) {
      toast({
        colorScheme: 'red',
        title: 'error',
        description: 'An error occured: ' + e['message'],
      });
    }
  };

  const handleSubmit = methods.handleSubmit(onValid);

  return (
    <SimpleForm handleSubmit={handleSubmit} submitOnBlur={false} {...methods}>
      <Drawer size="full" isOpen={isOpen} onClose={onClose}>
        <DrawerOverlay />

        <DrawerContent>
          <DrawerHeader display="flex" justifyContent="space-between">
            <Heading>Snippet</Heading>{' '}
            <IconButton aria-label="close" onClick={onClose} icon={<HiXMark />} colorScheme="red" />
          </DrawerHeader>
          <DrawerBody>
            <FilterEditor rootRef={condition} base={base?.filter} sources={sources} />
          </DrawerBody>
          <DrawerFooter gap={2}>
            <SimpleInput placeholder="Unique Name" w="30%" name="identifier" />
            <SimpleInput placeholder="Description" w="60%" name="description" />
            <Button w={24} isLoading={isLoading} type="submit" onClick={handleSubmit} colorScheme="primary">
              Save
            </Button>
          </DrawerFooter>
        </DrawerContent>
      </Drawer>
    </SimpleForm>
  );
};

export const SnippetsScreen = () => {
  const sourcesModal = useDisclosure();
  const snippetModal = useDisclosure();

  const { query, handleQueryChange, filters, setFilters, sorting, setSorting, pagination, setPagination } = useCrud();

  const { data: snippets, refetch } = trpc.getSnippets.useQuery({ sources: [] });

  const [selectedSnippet, setSelectedSnippet] = useState<FilterExpressionSnippet>(null);
  const [selectedSources, setSelectedSources] = useState<Filters.DataSources[]>([]);

  const adjustSources = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      const value = event.target.value;
      if (selectedSources.includes(value as Filters.DataSources)) {
        setSelectedSources(selectedSources.filter((source) => source !== value));
      } else {
        setSelectedSources([...selectedSources, value as Filters.DataSources]);
      }
    },
    [selectedSources],
  );

  // Todo at some point
  const { mutateAsync: deleteSnippet } = trpc.deleteSnippet.useMutation();

  const handleClear = () => {
    setFilters([]);
  };

  const onRowClick = useCallback(
    (row: FilterExpressionSnippet) => {
      setSelectedSnippet(row);
      setSelectedSources([...row.sources]);
      snippetModal.onOpen();
    },
    [snippetModal],
  );
  const toast = useToast();

  const onDelete = useCallback(
    (row) => {
      return async () => {
        try {
          await deleteSnippet({
            identifier: row.identifier,
          });
        } catch (e) {
          toast({
            colorScheme: 'red',
            title: 'error',
            description: 'An error occured: ' + e['message'],
          });
        }

        refetch();
      };
    },
    [deleteSnippet, toast, refetch],
  );

  const columnHelper = createColumnHelper<FilterExpressionSnippet & { action?: any }>();
  const columns = useMemo(
    () => [
      columnHelper.accessor('id', {
        header: 'ID',
        size: Number.MIN_SAFE_INTEGER,
      }),
      columnHelper.accessor('identifier', {
        header: 'Identifier',
      }),
      columnHelper.accessor('description', {
        header: 'Description',
      }),
      columnHelper.accessor('sources', {
        header: 'Sources',
        cell: (cell) => cell.row.original.sources.join(','),
      }),
      columnHelper.accessor('action', {
        header: 'Actions',
        cell: (cell) => {
          const row = cell.row.original;
          
          return (
            <Box onClick={(e) => e.stopPropagation()}>
              <Menu size="xs">
                <MenuButton
                  as={IconButton}
                  aria-label="Options"
                  size="sm"
                  icon={<HiEllipsisVertical />}
                  variant="ghost"
                />
                <MenuList>
                  <MenuItem onClick={() => onRowClick(row)}>Edit</MenuItem>
                  <MenuItem onClick={onDelete(row)}>Delete</MenuItem>
                </MenuList>
              </Menu>
            </Box>
          );
        },
      }),
    ],
    [columnHelper, onDelete, onRowClick],
  );

  return (
    <Stack spacing="0" h="full">
      <Stack p="4" spacing="4">
        <Stack direction="row" justify="space-between" zIndex="99" w="full">
          <Stack direction="row" align="center" w="full">
            <Heading variant="h3">Filter Snippets</Heading>
            <DataTableSearch value={query} onChange={handleQueryChange} />
          </Stack>
          <Button
            colorScheme="brand"
            onClick={() => {
              setSelectedSources([]);
              setSelectedSnippet(undefined);
              sourcesModal.onOpen();
            }}
          >
            Create Snippet
          </Button>
        </Stack>
        <Show if={filters.length > 0}>
          <DataTableFilters filters={filters} setFilters={setFilters} onClear={handleClear} />
        </Show>
      </Stack>
      <DataTable
        columns={columns}
        data={snippets ?? []}
        dataCount={snippets?.length ?? 0}
        pagination={pagination}
        setPagination={setPagination}
        sorting={sorting}
        setSorting={setSorting}
        onRowClick={onRowClick}
      />
      <Modal {...sourcesModal}>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Choose sources for snippet</ModalHeader>
          <ModalBody>
            <List>
              {Object.keys(Filters.dataSource).map((key) => (
                <ListItem key={key} display="flex" alignContent="center" gap={1}>
                  <Checkbox onChange={adjustSources} value={key} /> {key}
                </ListItem>
              ))}
            </List>
          </ModalBody>
          <ModalFooter>
            <Button
              onClick={() => {
                sourcesModal.onClose();
                snippetModal.onOpen();
              }}
            >
              Create
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
      <Show if={snippetModal.isOpen}>
        <SnippetModal
          base={selectedSnippet}
          sources={selectedSources}
          isOpen={snippetModal.isOpen}
          onClose={snippetModal.onClose}
          refetch={refetch}
        />
      </Show>
    </Stack>
  );
};
